import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { getAuthProviders, getMe, logout, startLogin } from './api/client'
import type { AuthMe, AuthProvider as Anmeldeanbieter } from './api/types'
import type { Textschluessel } from './i18n'

/**
 * Die Anmeldung im Laden.
 *
 * Freiwillig, und das bleibt sie: Bestellen geht weiter ohne Konto. Wer sich
 * anmeldet, bekommt zwei Dinge — einen Warenkorb, der das Gerät wechselt, und
 * eine Liste seiner Bestellungen, ohne Nummer und E-Mail tippen zu müssen.
 *
 * Welche Anbieter es gibt, sagt der Laden. Eine leere Liste heißt: keine
 * Anmeldung anbieten — dann steht in der Kopfzeile nichts, und niemand sucht
 * ein Konto, das es hier nicht gibt.
 */
interface Anmeldung {
  anbieter: Anmeldeanbieter[]
  /** Bis die erste Antwort da ist: niemand angemeldet, aber auch nichts anzubieten. */
  ich: AuthMe
  laedt: boolean
  /** Startet die Anmeldung — eine Seitennavigation, kein Aufruf. */
  anmelden: (key: string, zurueckNach: string) => void
  abmelden: () => Promise<void>
}

const ABGEMELDET: AuthMe = { signedIn: false }

const AuthContext = createContext<Anmeldung | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [anbieter, setAnbieter] = useState<Anmeldeanbieter[]>([])
  const [ich, setIch] = useState<AuthMe>(ABGEMELDET)
  const [laedt, setLaedt] = useState(true)

  const lebt = useRef(true)
  useEffect(() => {
    lebt.current = true
    return () => {
      lebt.current = false
    }
  }, [])

  useEffect(() => {
    const steuerung = new AbortController()

    // Beides zusammen und beides nachsichtig: Ein Laden ohne Anmeldung
    // antwortet mit einer leeren Liste, ein alter Server womöglich mit 404 —
    // in beiden Fällen soll der Laden verkaufen und nicht eine Meldung zeigen.
    Promise.all([
      getAuthProviders(steuerung.signal).catch(() => [] as Anmeldeanbieter[]),
      getMe(steuerung.signal).catch(() => ABGEMELDET),
    ])
      .then(([liste, wer]) => {
        if (!lebt.current) return
        setAnbieter(liste)
        setIch(wer)
      })
      .finally(() => {
        if (lebt.current) setLaedt(false)
      })

    return () => steuerung.abort()
  }, [])

  const anmelden = useCallback((key: string, zurueckNach: string) => {
    startLogin(key, zurueckNach)
  }, [])

  const abmelden = useCallback(async () => {
    try {
      await logout()
    } finally {
      // Auch nach einem Fehlschlag abgemeldet anzeigen: Der Server hat das
      // Cookie entweder gelöscht oder er ist nicht erreichbar — in beiden
      // Fällen ist „noch angemeldet" die falschere der beiden Auskünfte.
      if (lebt.current) setIch(ABGEMELDET)
    }
  }, [])

  const wert = useMemo<Anmeldung>(
    () => ({ anbieter, ich, laedt, anmelden, abmelden }),
    [anbieter, ich, laedt, anmelden, abmelden],
  )

  return <AuthContext.Provider value={wert}>{children}</AuthContext.Provider>
}

export function useAuth(): Anmeldung {
  const anmeldung = useContext(AuthContext)
  if (!anmeldung) throw new Error('useAuth außerhalb des AuthProvider verwendet')
  return anmeldung
}

/**
 * Was der Server über eine misslungene Anmeldung sagt.
 *
 * Er schickt den Kunden mit `?anmeldung=…` auf die Startseite zurück — eine
 * eigene Fehlerseite wäre falsch, denn meistens hat er nur abgebrochen.
 *
 * Der Server schickt nur das Stichwort, den Satz bildet das Ladenfenster —
 * deshalb ist das einer der wenigen Serverfälle, die sehr wohl übersetzt
 * gehören. `t` kommt als Streitwert herein und nicht aus einem Haken: Die
 * Funktion ist kein Bauteil und soll auch keines werden.
 */
export function anmeldemeldung(
  grund: string | null,
  t: (schluessel: Textschluessel) => string,
): string | null {
  switch (grund) {
    case 'abgebrochen':
      return t('anmeldung.abgebrochen')
    case 'abgelaufen':
      return t('anmeldung.abgelaufen')
    case 'fehlgeschlagen':
      return t('anmeldung.fehlgeschlagen')
    default:
      return null
  }
}
